import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { GetParamsObject } from '@/model/query-params';
import { breadcrumb } from './breadcrumb';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { VideoChannelFilters } from './types';
import { VideoChannelTable } from './components/VideoChannelTable/VideoChannelTable';
import { useNavigate } from 'react-router';
import { VideoChannelFilterBox } from './components/VideoChannelFilterBox';
import { useDeleteLiveVideoMutation, useDeleteVideoMutation, useGetVideoById, useVideo } from '@/services/queries/video';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { CategoryVideo, Video } from '@/model';
import { VideoEditModal } from './components/VideoEditModal';
import { VideoCreateModal } from './components/VideoCreateModal';
import { useCategoryVideo } from '@/services/queries/category-video';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationVideosChannel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.VIDEOS_CHANNEL,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_videos_channel.application_videos_channel_enable_success')
      : t('pages.application_videos_channel.application_videos_channel_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_videos_channel.application_videos_channel_enable_error')
      : t('pages.application_videos_channel.application_videos_channel_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.VIDEOS_CHANNEL, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Video | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeleteVideoMutation();
  const deleteLiveMutation = useDeleteLiveVideoMutation();
  const getItemByUUID = useGetVideoById();

  const { data: categoryVideoData } = useCategoryVideo(GetParamsObject({}, 1, 900, 'asc', 'created_at'));
  const categoryVideos = useMemo(() => {
    return Array.isArray(categoryVideoData?.data) ? (categoryVideoData?.data as CategoryVideo[]) : [];
  }, [categoryVideoData]);

  const filtersForm = useFiltersForm<VideoChannelFilters>({
    defaultValues: {
      title: '',
      team_uuid: '',
      category_uuid: '',
      end_date: '',
      start_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useVideo(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Video) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    if (selectedItem?.uuid === 'LIVE_VIDEO_CATEGORY') {
      deleteLiveMutation.mutate(undefined, {
        onSuccess: () => {
          toast({
            title: t('pages.application_videos_channel.application_videos_channel_delete_live_video_success'),
            status: 'success',
          });
          forceRefetch();
          setSelectedItem(undefined);
        },
        onError: (resp: any) => {
          setSelectedItem(undefined);
          toast({
            status: 'error',
            title:
              (resp && resp.message) || t('pages.application_videos_channel.application_videos_channel_delete_live_video_error'),
          });
        },
      });
      return;
    }

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_videos_channel.application_videos_channel_delete_video_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_videos_channel.application_videos_channel_delete_video_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_videos_channel.application_videos_channel_video_not_found_error'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_videos_channel.application_videos_channel_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_videos_channel.application_videos_channel_label')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_videos_channel.application_videos_channel_enabled')
                    : t('pages.application_videos_channel.application_videos_channel_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button onClick={() => navigate('/application/videos-channel/categories')}>
              {t('pages.application_videos_channel.application_videos_channel_view_categories_button')}
            </Button>

            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsCreateModalOpen(true)}
              w={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_videos_channel.application_videos_channel_create_video_button')}
            </Button>
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <VideoChannelFilterBox
            clubs={clubs}
            filtersForm={filtersForm}
            forceRefetch={forceRefetch}
            categoryVideos={categoryVideos}
          />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <VideoChannelTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <VideoEditModal
          isOpen={!!isEditModalOpen}
          categoryVideos={categoryVideos}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <VideoCreateModal
        categoryVideos={categoryVideos}
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_videos_channel.application_videos_channel_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_videos_channel.application_videos_channel_delete_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_videos_channel.application_videos_channel_app_area_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_videos_channel.application_videos_channel_app_area_confirm')
              : t('pages.application_videos_channel.application_videos_channel_app_area_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
